import { useState } from 'react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useTeam, type CreateStaffInput } from '@/hooks/useTeam';
import { useLocations } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';

interface CreateStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateStaffDialog({ open, onOpenChange, onSuccess }: CreateStaffDialogProps) {
  const { createStaff } = useTeam();
  const { locations } = useLocations();
  const { isSuperadmin, isPrimaryAdmin } = useAuth();
  
  const [formData, setFormData] = useState<CreateStaffInput>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    pin: '',
    location_ids: [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.pin && formData.pin.length !== 6) {
      setError('PIN must be exactly 6 digits');
      return;
    }

    try {
      await createStaff.mutateAsync(formData);
      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Failed to create staff member');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'employee',
      pin: '',
      location_ids: [],
    });
    setError(null);
  };

  const handleLocationToggle = (locationId: string) => {
    setFormData(prev => ({
      ...prev,
      location_ids: prev.location_ids?.includes(locationId)
        ? prev.location_ids.filter(id => id !== locationId)
        : [...(prev.location_ids || []), locationId],
    }));
  };

  return (
    <ResponsiveModal open={open} onOpenChange={(value) => {
      if (!value) resetForm();
      onOpenChange(value);
    }}>
      <ResponsiveModalContent maxWidth="md">
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Add New Staff Member</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Create a new account for a team member. They will receive an email to confirm their account.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Temporary Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min. 8 characters"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Staff member will be asked to change this on first login
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value: CreateStaffInput['role']) => 
                      setFormData({ ...formData, role: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      {(isSuperadmin || isPrimaryAdmin) && (
                        <>
                          <SelectItem value="owner">Owner</SelectItem>
                          <SelectItem value="primary_admin">Business Owner</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  {formData.role === 'primary_admin' && (
                    <p className="text-xs text-amber-600">
                      Business Owner has full access to all business features
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pin">PIN (optional)</Label>
                  <Input
                    id="pin"
                    type="password"
                    maxLength={6}
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                    placeholder="6 digits"
                  />
                </div>
              </div>

              {locations.length > 0 && (
                <div className="space-y-2">
                  <Label>Location Access</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                    {locations.map((location) => (
                      <label
                        key={location.id}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.location_ids?.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          className="rounded border-input"
                        />
                        <span className="text-sm">{location.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="blaze"
              disabled={createStaff.isPending}
            >
              {createStaff.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Staff Member
            </Button>
          </ResponsiveModalFooter>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
